/* eslint-disable max-lines */
import { TrashIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { FC, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { ICustomTripWorkingDayPattern } from "../../../interfaces/endpoints/patterns/ICustomTripWorkingDayPattern";
import { CustomTripWorkingDayType } from "../../../interfaces/endpoints/trips/Trips.types";
import { deletePattern } from "../../../services/CustomTripWorkingDayPattern.service";
import Button from "../../ui/Button";
import IconBtn from "../../ui/IconBtn";
import Modal from "../../ui/Modal";
import { SpinLoader } from "../../ui/SpinLoader";

interface ConfirmPatternModalProps {
  show: boolean;
  onClose: () => void;
  pattern: ICustomTripWorkingDayPattern | null;
  onConfirm: (pattern: ICustomTripWorkingDayPattern) => void;
  onPatternDeleted?: () => void;
}

const ConfirmPatternModal: FC<ConfirmPatternModalProps> = ({
  show,
  onClose,
  pattern,
  onConfirm,
  onPatternDeleted
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const handleDelete = async () => {
    if (!pattern) return;

    setIsDeleting(true);
    try {
      await deletePattern(pattern.id!);
      toast.success("Pattern-i u fshi me sukses");
      onPatternDeleted?.();
      onClose();
    } catch (error) {
      toast.error("Ndodhi një gabim gjatë fshirjes së pattern-it");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirm = () => {
    if (!pattern) return;
    setIsApplying(true);
    try {
      onConfirm(pattern);
    } finally {
      setIsApplying(false);
    }
  };

  const dateRanges = useMemo(() => {
    if (!pattern?.dates) return [];

    const sortedDates = pattern.dates
      .map((d) => new Date(d.date))
      .sort((a, b) => a.getTime() - b.getTime());

    if (sortedDates.length === 0) return [];

    const ranges: { start: Date; end: Date }[] = [];
    let rangeStart = sortedDates[0];
    let rangeEnd = sortedDates[0];

    for (let i = 1; i < sortedDates.length; i++) {
      const currentDate = sortedDates[i];
      const dayDiff = (currentDate.getTime() - rangeEnd.getTime()) / (1000 * 60 * 60 * 24);

      if (dayDiff <= 1) {
        rangeEnd = currentDate;
      } else {
        ranges.push({
          start: rangeStart,
          end: rangeEnd
        });
        rangeStart = currentDate;
        rangeEnd = currentDate;
      }
    }

    ranges.push({
      start: rangeStart,
      end: rangeEnd
    });

    return ranges;
  }, [pattern?.dates]);

  if (!pattern) return null;

  const getTypeLabel = (type: CustomTripWorkingDayType) => {
    return type === CustomTripWorkingDayType.ExceptionDate
      ? "Përjashtim"
      : "Datë pune";
  };

  return (
    <Modal show={show} onClose={onClose} position="center" hasHeader={false} calcHeight={false}
    buttonElement={
      <Button
        onClick={handleConfirm}
        disabled={isApplying || isDeleting}
        className="flex items-center justify-center min-w-[100px]"
      >
        {isApplying ? (
          <SpinLoader formatClassName="w-4 h-4" />
        ) : (
          "Vazhdo"
        )}
      </Button>
    }
    >
    <div className="p-6 w-full">
      <div className="flex justify-between items-center border-b pb-4">
        <h2 className="text-lg font-medium text-gray-900">
          Dëshironi të aplikoni "{pattern.name}"?
        </h2>
        <IconBtn
          onClick={handleDelete}
          className={`${(isDeleting || isApplying) ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {isDeleting ? (
            <SpinLoader formatClassName="w-5 h-5 text-red-500" />
          ) : (
            <TrashIcon className="w-5 h-5 text-red-500" />
          )}
        </IconBtn>
      </div>

      <div className="mt-4">
        <p className="text-sm text-gray-600">Lloji i datave</p>
        <p className="text-md font-medium mt-1 text-gray-900">
          {getTypeLabel(pattern.type)}
        </p>
      </div>

      <div className="mt-4">
        <p className="text-sm text-gray-600">Datat</p>
        <div className="flex flex-wrap gap-5 mt-2">
          {dateRanges.map((range, index) => (
            <span key={index} className="whitespace-nowrap text-sm text-gray-900">
              {format(range.start, "dd/MM/yyyy")}
              {range.start.getTime() !== range.end.getTime() &&
                ` - ${format(range.end, "dd/MM/yyyy")}`}
            </span>
          ))}
        </div>
      </div>
    </div>
  </Modal>
  );
};

export default ConfirmPatternModal;
